import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { motion, useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import styles from "./ByHeartPaywall.module.scss"

/**
 * ByHeartPaywall — an original, self-contained NON-ONBOARDING upsell built on a
 * behavioral driver that no prior paywall used as its spine: **progress-
 * completion (goal-gradient / endowed progress)**, dressed only in features the
 * app really has (automatic full-library backup, full-resolution originals,
 * private & hidden, synced on every device).
 *
 * Divergence brief (see claude-paywalls/_ledger.md — 27 priors):
 *   - Layout:      a HEART built as a photo MOSAIC (emergent image forming an
 *                  icon). No prior did a mosaic-that-forms-a-shape; not a
 *                  hero+card / calendar / road / receipt / map / book / chat /
 *                  editor / player / network / line / channel / net / stage.
 *   - Interaction: FREE-FORM 2D SWEEP over the shape — drag across the heart and
 *                  the faint, not-yet-kept moments bloom into full colour under
 *                  your finger; a live "kept" counter climbs and the clasp locks
 *                  shut when the heart is whole. (First 2D paint-over-a-shape
 *                  gesture; prior gestures were 1D scrubs, toggles, holds, flips,
 *                  taps, page-turns.)
 *   - Register:    tender / warm / heartfelt completeness (not fear, luxury,
 *                  playful-loud, wanderlust).
 *   - Art:         light warm ivory + PHOTOGRAPHIC mosaic (Unsplash roll: use);
 *                  brand blue = the kept/protected state + locket clasp; a soft
 *                  amber warmth on the moment of keeping; faint desaturated =
 *                  not-yet-kept.
 *   - Value:       COMPLETENESS — "keep every moment, not just some" — full-res
 *                  originals, private, auto-backed-up on every device. Zeigarnik
 *                  pull: the heart begs to be finished.
 *
 * Honest & compliant: the faint cells are the same memories, just "not yet in
 * your vault in full quality" — never a claim that anything is lost. Real
 * features only, real savings arithmetic, no fake countdown or scarcity. Yearly
 * is the featured default plan and carries the most prominent price; monthly is
 * small and secondary.
 */

// Photos are Unsplash demo content bundled at build time (public/byheart/*).
const PHOTO_COUNT = 18
const photoFor = (i: number) =>
  `m${String((i % PHOTO_COUNT) + 1).padStart(2, "0")}`

// Deterministic 0..1 hash so the mosaic + its starting fill are stable across
// renders and the static build (no runtime randomness).
const hash = (n: number) => {
  const x = Math.sin(n * 127.1 + 31.7) * 43758.5453
  return x - Math.floor(x)
}

type Cell = { r: number; c: number; idx: number; photo: string }

// Build a heart from a grid of squares via the classic heart implicit curve,
// then trim empty rows/cols so it sits flush.
function buildHeart(cols: number, rows: number) {
  const raw: { r: number; c: number }[] = []
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const nx = ((c + 0.5) / cols) * 2 - 1
      const ny = ((r + 0.5) / rows) * 2 - 1
      const x = nx * 1.3
      const y = -ny * 1.3 + 0.72
      const v = Math.pow(x * x + y * y - 1, 3) - x * x * Math.pow(y, 3)
      if (v <= 0) raw.push({ r, c })
    }
  }
  const minR = Math.min(...raw.map((p) => p.r))
  const minC = Math.min(...raw.map((p) => p.c))
  const maxC = Math.max(...raw.map((p) => p.c))
  const width = maxC - minC + 1
  const cells: Cell[] = raw
    .map((p) => ({ r: p.r - minR, c: p.c - minC }))
    .sort((a, b) => a.r - b.r || a.c - b.c)
    .map((p, idx) => ({ ...p, idx, photo: photoFor(idx * 7) }))
  const height = Math.max(...cells.map((p) => p.r)) + 1
  return { cells, width, height }
}

const HEART = buildHeart(13, 12)

// ~56% of the heart starts "kept" — enough that finishing feels within reach
// (goal-gradient) but the gaps are obvious enough to pull the eye.
const START_KEPT = HEART.cells.map((c) => hash(c.idx * 3.3 + 5) < 0.56)

type PlanKey = "yearly" | "monthly"

const SHACKLE_CLOSED = "M8 10.4V7a4 4 0 018 0v3.4"
const SHACKLE_OPEN = "M8 10.4V7a4 4 0 017.6-1.7"
const Lock = ({ open }: { open: boolean }) => (
  <svg viewBox="0 0 24 24" width="100%" height="100%" aria-hidden="true">
    <motion.path
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      initial={false}
      animate={{ d: open ? SHACKLE_OPEN : SHACKLE_CLOSED }}
      transition={{ type: "spring", stiffness: 200, damping: 18 }}
    />
    <rect
      x="5.2"
      y="10"
      width="13.6"
      height="10.6"
      rx="2.6"
      fill="currentColor"
    />
    <circle cx="12" cy="14.6" r="1.5" fill="#fff" />
    <rect x="11.2" y="15.2" width="1.6" height="3" rx="0.8" fill="#fff" />
  </svg>
)

const ByHeartPaywall = () => {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    const y = yearlyProduct?.price ?? 35.99
    const m = monthlyProduct?.price ?? 13.99
    if (!m) return 0
    return Math.round((1 - y / (m * 12)) * 100)
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")
  const [kept, setKept] = useState<boolean[]>(START_KEPT)

  const total = HEART.cells.length
  const keptCount = useMemo(() => kept.filter(Boolean).length, [kept])
  const complete = keptCount >= total
  const pct = keptCount / total

  const draggingRef = useRef(false)
  const stageRef = useRef<HTMLDivElement>(null)
  const autoRef = useRef(false)
  const wasComplete = useRef(false)

  const keepCell = useCallback((idx: number) => {
    setKept((k) => {
      if (k[idx]) return k
      const next = [...k]
      next[idx] = true
      return next
    })
  }, [])

  // Resolve the cell under a pointer position and keep it.
  const keepAtPoint = useCallback(
    (x: number, y: number) => {
      const el = document.elementFromPoint(x, y)
      const cell = el?.closest("[data-idx]") as HTMLElement | null
      if (cell) keepCell(Number(cell.dataset.idx))
    },
    [keepCell],
  )

  const onPointerDown = (e: React.PointerEvent) => {
    if (complete) return
    draggingRef.current = true
    stageRef.current?.setPointerCapture?.(e.pointerId)
    keepAtPoint(e.clientX, e.clientY)
  }
  const onPointerMove = (e: React.PointerEvent) => {
    if (!draggingRef.current) return
    // Coalesced events give a smooth continuous stroke on fast sweeps.
    const evts = (e.nativeEvent as any).getCoalescedEvents?.() ?? [
      e.nativeEvent,
    ]
    for (const ev of evts) keepAtPoint(ev.clientX, ev.clientY)
  }
  const endDrag = () => {
    draggingRef.current = false
  }

  const keepAll = useCallback(() => {
    setKept(HEART.cells.map(() => true))
  }, [])

  // Auto-demo: a gentle wave blooms a few of the faint moments to teach the
  // sweep, then hands the gesture over. Never completes it for them.
  useEffect(() => {
    if (autoRef.current || reduceMotion) return
    autoRef.current = true
    const faint = HEART.cells
      .map((c) => c.idx)
      .filter((i) => !START_KEPT[i])
      .sort((a, b) => hash(a) - hash(b))
      .slice(0, 5)
    const timers = faint.map((idx, k) =>
      window.setTimeout(() => keepCell(idx), 900 + k * 230),
    )
    return () => timers.forEach(clearTimeout)
  }, [reduceMotion, keepCell])

  // Soft haptic-ish glow pulse the instant the heart becomes whole.
  const [justSealed, setJustSealed] = useState(false)
  useEffect(() => {
    if (complete && !wasComplete.current) {
      wasComplete.current = true
      setJustSealed(true)
      const t = window.setTimeout(() => setJustSealed(false), 1200)
      return () => clearTimeout(t)
    }
    if (!complete) wasComplete.current = false
  }, [complete])

  const handleClose = () => native.postNativeEvent("close")
  const handleSubscribe = () => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else keepAll()
  }

  return (
    <div className={styles.root}>
      <header className={styles.top}>
        <div className={styles.brand}>
          <span className={styles.brandMark} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="16" height="16">
              <path
                d="M12 2.6l6.6 2.5v5.2c0 4.3-2.8 7.7-6.6 9.1-3.8-1.4-6.6-4.8-6.6-9.1V5.1L12 2.6z"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinejoin="round"
              />
              <path
                d="M9.2 12.1l2 2 3.6-3.9"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.7"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
          <span className={styles.brandText}>
            Private Photo Vault
            <em>Premium</em>
          </span>
        </div>
        <button
          className={styles.close}
          onClick={handleClose}
          aria-label="Close"
        >
          <svg viewBox="0 0 24 24" width="15" height="15" aria-hidden="true">
            <path
              d="M5 5l14 14M19 5L5 19"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      <div className={styles.intro}>
        <span className={styles.eyebrow}>Your memories, whole</span>
        <h1 className={styles.title}>
          Keep every moment.
          <b> Not just some.</b>
        </h1>
        <p className={styles.sub}>
          You already have some safe. Premium keeps the whole heart — every
          photo &amp; video in full quality, private, backed up on every device.
        </p>
      </div>

      {/* Progress readout */}
      <div className={styles.meter}>
        <div className={styles.meterLine}>
          <span className={styles.count}>
            <b>{keptCount}</b>
            <span> / {total} moments kept</span>
          </span>
          <span
            className={`${styles.state} ${complete ? styles.stateDone : ""}`}
          >
            {complete
              ? "Whole — every moment kept"
              : `${total - keptCount} still faint`}
          </span>
        </div>
        <div className={styles.track}>
          <motion.span
            className={styles.fill}
            animate={{ width: `${Math.round(pct * 100)}%` }}
            transition={{ type: "spring", stiffness: 140, damping: 22 }}
          />
        </div>
      </div>

      {/* The heart — a mosaic of your life you sweep whole */}
      <div className={styles.heartWrap}>
        <div
          ref={stageRef}
          className={`${styles.stage} ${complete ? styles.stageDone : ""} ${
            justSealed ? styles.stageSeal : ""
          } ${reduceMotion ? styles.stageStill : ""}`}
          style={
            {
              "--cols": HEART.width,
              "--rows": HEART.height,
            } as React.CSSProperties
          }
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          onPointerLeave={endDrag}
          role="group"
          aria-label="Your memories, as a heart. Sweep across it to keep every moment."
        >
          <div className={styles.mosaic}>
            {HEART.cells.map((cell) => {
              const on = kept[cell.idx]
              return (
                <button
                  key={cell.idx}
                  data-idx={cell.idx}
                  className={`${styles.cell} ${on ? styles.cellOn : ""}`}
                  style={{ gridRow: cell.r + 1, gridColumn: cell.c + 1 }}
                  onClick={() => keepCell(cell.idx)}
                  tabIndex={-1}
                  aria-hidden="true"
                >
                  <img
                    src={`/byheart/${cell.photo}.jpg`}
                    alt=""
                    draggable={false}
                  />
                  <span className={styles.grain} />
                </button>
              )
            })}
          </div>

          {/* Locket clasp at the top cleft — locks shut when the heart is whole */}
          <div className={`${styles.clasp} ${complete ? styles.claspOn : ""}`}>
            <Lock open={!complete} />
          </div>
        </div>

        <div className={styles.hint}>
          {complete ? (
            <span className={styles.hintDone}>
              <svg
                viewBox="0 0 24 24"
                width="15"
                height="15"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4.2 4.3L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.6"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              This is what Premium keeps — automatically, forever
            </span>
          ) : (
            <>
              <span className={styles.finger} aria-hidden="true" />
              Sweep across your memories to keep the faint ones
            </>
          )}
        </div>
      </div>

      {/* Three honest, real features */}
      <div className={styles.feats}>
        {[
          { t: "Full quality", s: "Originals, never squeezed" },
          { t: "Private", s: "PIN & Face ID — only you" },
          { t: "Everywhere", s: "In step on every device" },
        ].map((f) => (
          <div key={f.t} className={styles.feat}>
            <b>{f.t}</b>
            <span>{f.s}</span>
          </div>
        ))}
      </div>

      {/* Pricing — yearly featured, monthly secondary */}
      <div className={styles.plans}>
        <button
          className={`${styles.planYear} ${plan === "yearly" ? styles.planYearOn : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span
            className={`${styles.radio} ${plan === "yearly" ? styles.radioOn : ""}`}
          >
            {plan === "yearly" && (
              <svg
                viewBox="0 0 24 24"
                width="12"
                height="12"
                aria-hidden="true"
              >
                <path
                  d="M5 12.5l4.2 4.3L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="3"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </span>
          <span className={styles.planBody}>
            <span className={styles.planName}>
              Yearly
              {savingsPct > 0 && (
                <span className={styles.badge}>SAVE {savingsPct}%</span>
              )}
            </span>
            <span className={styles.planTrial}>
              {hasTrial
                ? "7-day free trial, then billed yearly"
                : "Billed once a year"}
            </span>
          </span>
          <span className={styles.planPrice}>
            <b>{yearlyPrice}</b>
            <span>≈ {yearlyPerMonth}/mo</span>
          </span>
        </button>

        <button
          className={`${styles.planMonth} ${plan === "monthly" ? styles.planMonthOn : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          Prefer monthly? <u>{monthlyPrice}/mo</u>
        </button>
      </div>

      <div className={styles.footerLinks}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span>·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </div>

      <p className={styles.credit}>Demo photos from Unsplash</p>

      {/* Sticky CTA */}
      <div className={styles.dock}>
        <button className={styles.cta} onClick={handleSubscribe}>
          <span className={styles.ctaMain}>
            {plan === "yearly" && hasTrial
              ? "Keep every one — free for 7 days"
              : "Keep every one"}
            <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
              <path
                d="M12 21C6.5 17 3 13.5 3 9.2A4.2 4.2 0 0112 6a4.2 4.2 0 019 3.2C21 13.5 17.5 17 12 21z"
                fill="currentColor"
              />
            </svg>
          </span>
          <span className={styles.ctaSub}>
            {plan === "yearly"
              ? `${yearlyPrice}/yr after trial · cancel anytime`
              : `${monthlyPrice}/mo · cancel anytime`}
          </span>
          {!reduceMotion && (
            <motion.span
              className={styles.shine}
              initial={{ left: "-55%" }}
              animate={{ left: "120%" }}
              transition={{
                duration: 2.6,
                repeat: Infinity,
                repeatDelay: 2.8,
                ease: "easeInOut",
              }}
            />
          )}
        </button>
      </div>
    </div>
  )
}

export default ByHeartPaywall
